/**
 * Directory API Hook
 *
 * Custom hook for managing directory API operations with React patterns.
 * Provides employee CRUD operations using the centralized apiService.
 * Follows dashboard patterns for consistency across the application.
 */

import { useState, useCallback } from 'react';
import { useAPIService, APIError } from '../../../services/apiService';

// Employee interface matching the existing component structure
export interface Employee {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  email_tec?: string;
  job?: string;
  role?: string;
  department?: string;
  status?: 'active' | 'inactive' | 'pending';
  startDate?: string;
  facility?: string;
  locations?: string[];
  phone?: string;
  phone_primary?: string;
  phone_work?: string;
  phone_number?: string;
  phoneNumber?: string;
  lastLogin?: string;
  permissions?: string[];
}

// Directory-specific filter payload for API requests
export interface DirectoryFiltersPayload {
  search?: string;
  location?: string;
  jobTitle?: string;
  department?: string;
  status?: string;
  facility?: string;
}

// Employee creation/update payload
export interface EmployeePayload {
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  role: string;
  facility: string;
  status?: 'active' | 'inactive' | 'pending';
  phone?: string;
  permissions?: string[];
}

/**
 * Directory API service hook
 * Provides centralized employee data management with loading states
 */
export const useDirectoryApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiService = useAPIService();

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle API errors consistently
   */
  const handleError = useCallback((err: unknown, fallbackMessage: string) => {
    let errorMessage = fallbackMessage;

    if (err instanceof APIError) {
      errorMessage = err.message;
    } else if (err instanceof Error) {
      errorMessage = err.message;
    }

    setError(errorMessage);
    console.error(`Directory API Error: ${errorMessage}`, err);
  }, []);

  /**
   * Fetch all employees (active only)
   * Endpoint: POST /directory/directory_get_employee_list
   * Django filters for active=True, user__isnull=False
   */
  const fetchEmployees = useCallback(async (): Promise<Employee[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<{ employees: any[], locations: string[] }>(
        '/directory/directory_get_employee_list',
        {},
        'Loading employees...'
      );

      // Transform Django response to match Employee interface
      const employees = (response.data?.employees || []).map((emp: any) => ({
        id: emp.id?.toString() || '',
        name: emp.name || '',
        firstName: emp.name?.split(' ')[0] || '',
        lastName: emp.name?.split(' ').slice(1).join(' ') || '',
        email: emp.email_tec || emp.email_personal || '',
        email_tec: emp.email_tec,
        phone: emp.phone_mobile || emp.phone_home || '',
        job: emp.job || '',
        role: emp.job || '',
        locations: emp.locations || [],
        facility: emp.locations?.[0] || '',
        status: 'active' as const, // Django already filters for active=True
        department: emp.job || ''
      }));

      return employees;
    } catch (err) {
      // Check if it's a 404 or endpoint doesn't exist
      if (err instanceof APIError && (err.status === 404 || err.status === 0)) {
        // Return empty array instead of throwing - let the component handle with mock data
        return [];
      }

      handleError(err, 'Failed to fetch employees');
      // Still return empty array to allow fallback to mock data
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Fetch employees with filters
   * Endpoint: POST /api/employees/search or similar
   */
  const fetchEmployeesFiltered = useCallback(async (filters: DirectoryFiltersPayload): Promise<Employee[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<Employee[]>('/api/employees/search', filters, 'Searching employees...');

      return response.data || [];
    } catch (err) {
      handleError(err, 'Failed to search employees');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Get single employee by ID
   * Endpoint: GET /api/employees/{id}
   */
  const fetchEmployee = useCallback(async (employeeId: string): Promise<Employee> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.get<Employee>(`/api/employees/${employeeId}`, 'Loading employee details...');

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to fetch employee details');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Create new employee
   * Endpoint: POST /api/employees
   */
  const createEmployee = useCallback(async (employeeData: EmployeePayload): Promise<Employee> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<Employee>('/api/employees', employeeData, 'Creating employee...');

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to create employee');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Update existing employee
   * Endpoint: PUT /api/employees/{id}
   */
  const updateEmployee = useCallback(async (employeeId: string, employeeData: Partial<EmployeePayload>): Promise<Employee> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.put<Employee>(`/api/employees/${employeeId}`, employeeData, 'Updating employee...');

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to update employee');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Delete employee
   * Endpoint: DELETE /api/employees/{id}
   */
  const deleteEmployee = useCallback(async (employeeId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await apiService.delete(`/api/employees/${employeeId}`, 'Deleting employee...');
    } catch (err) {
      handleError(err, 'Failed to delete employee');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Bulk update employee status
   * Endpoint: POST /api/employees/bulk-status
   */
  const bulkUpdateStatus = useCallback(async (employeeIds: string[], status: Employee['status']): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await apiService.post('/api/employees/bulk-status', {
        employee_ids: employeeIds,
        status
      }, 'Updating employee status...');
    } catch (err) {
      handleError(err, 'Failed to update employee status');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Export employees data
   * Endpoint: POST /api/employees/export
   * Returns blob for CSV download
   */
  const exportEmployees = useCallback(async (filters?: DirectoryFiltersPayload): Promise<Blob> => {
    try {
      setIsLoading(true);
      setError(null);

      // For blob endpoints, we need to handle manually like dashboard export
      const response = await fetch('/api/employees/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // CSRF token handling would be added here similar to dashboard export
        },
        credentials: 'include',
        body: JSON.stringify(filters || {}),
      });

      // Handle session expiration - redirect to login
      if (response.status === 401 || response.status === 403) {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
        throw new Error('Session expired');
      }

      if (!response.ok) {
        throw new APIError(`Export error: ${response.status} ${response.statusText}`, response.status);
      }

      return response.blob();
    } catch (err) {
      handleError(err, 'Failed to export employee data');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  /**
   * Get unique values for filter dropdowns
   * Processes employee data to extract unique departments, facilities, roles, etc.
   */
  const getFilterOptions = useCallback((employees: Employee[]) => {
    const departments = Array.from(new Set(employees.map(e => e.department).filter(Boolean)));
    const facilities = Array.from(new Set(employees.map(e => e.facility).filter(Boolean)));
    const roles = Array.from(new Set(employees.map(e => e.role || e.job).filter(Boolean)));
    const locations = Array.from(new Set(employees.flatMap(e => e.locations || [])));
    const statuses = Array.from(new Set(employees.map(e => e.status).filter(Boolean)));

    return {
      departments,
      facilities,
      roles,
      locations,
      statuses
    };
  }, []);

  /**
   * Transform employee data for compatibility with existing components
   * Maps between different Employee interface variations
   */
  const transformEmployeeData = useCallback((employees: Employee[]): Employee[] => {
    return employees.map(emp => ({
      ...emp,
      // Ensure firstName/lastName are set from name if needed
      firstName: emp.firstName || emp.name?.split(' ')[0] || '',
      lastName: emp.lastName || emp.name?.split(' ').slice(1).join(' ') || '',
      // Map email_tec to email if needed
      email: emp.email || emp.email_tec || '',
      // Map job to role if needed
      role: emp.role || emp.job || '',
      // Map various phone field names to phone
      phone: emp.phone || emp.phone_primary || emp.phone_work || emp.phone_number || emp.phoneNumber || '',
      // Ensure id is always a string
      id: emp.id || `emp-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    }));
  }, []);

  /**
   * Fetch employee details from Django endpoint
   * Endpoint: POST /directory/directory_get_employee
   */
  const fetchEmployeeDetails = useCallback(async (employeeId: string): Promise<any> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<any>('/directory/directory_get_employee',
        { id: employeeId },
        'Loading employee details...'
      );

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to fetch employee details');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Fetch employee credentials from Django endpoint
   * Endpoint: POST /hr/get_employee_credentials
   */
  const fetchEmployeeCredentials = useCallback(async (employeeId: string): Promise<any[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<{ credentials: any[] }>(
        '/hr/get_employee_credentials',
        { id: employeeId },
        'Loading employee credentials...'
      );

      return response.data?.credentials || [];
    } catch (err) {
      handleError(err, 'Failed to fetch employee credentials');
      // Return empty array on error - credentials are optional
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  return {
    // State
    isLoading,
    error,

    // Actions
    clearError,

    // Employee CRUD operations
    fetchEmployees,
    fetchEmployeesFiltered,
    fetchEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    bulkUpdateStatus,

    // Employee detail operations (Django-specific)
    fetchEmployeeDetails,
    fetchEmployeeCredentials,

    // Utility operations
    exportEmployees,
    getFilterOptions,
    transformEmployeeData
  };
};

export default useDirectoryApi;
